import { io, Socket } from 'socket.io-client';
import { 
  SocketConnectionStatus, 
  SocketServiceInterface,
  WebSocketConfig 
} from '../types/notification';
import { HospitalNotification, NotificationCallback } from '../types/hospital-notification';

class HospitalSocketService implements SocketServiceInterface {
  private socket: Socket | null = null;
  private connectionStatus: SocketConnectionStatus = {
    isConnected: false,
    isAuthenticated: false,
    reconnectAttempts: 0
  };
  private notificationCallbacks: NotificationCallback[] = [];
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private isReconnecting = false;
  private currentToken: string | null = null;
  private wasConnected = false;

  private readonly SERVER_URL = process.env.REACT_APP_API_URL?.replace('/api', '') 
    || 'http://192.168.219.115:4000'; // ✅ 수정: 올바른 백엔드 서버 주소와 포트 (4000)

  private readonly socketConfig: WebSocketConfig = {
    transports: ['polling', 'websocket'],
    timeout: 15000,
    autoConnect: false,
    reconnection: true,
    reconnectionAttempts: this.maxReconnectAttempts,
    reconnectionDelay: this.reconnectDelay,
    withCredentials: false,
    upgrade: true,
    forceNew: true
  };

  constructor() {
    this.initializeSocket();
    this.setupBrowserEventListeners();
  }

  private initializeSocket(): void {
    if (this.socket) return;
    console.log('🏥 Hospital Socket initializing to:', this.SERVER_URL);
    console.log('🔧 Socket Config:', {
      transports: this.socketConfig.transports,
      timeout: this.socketConfig.timeout,
      reconnectionAttempts: this.socketConfig.reconnectionAttempts,
      env: {
        REACT_APP_API_URL: process.env.REACT_APP_API_URL,
        REACT_APP_WS_URL: process.env.REACT_APP_WS_URL
      }
    });
    this.socket = io(this.SERVER_URL, this.socketConfig);
    this.setupSocketEventListeners();
  }  private setupSocketEventListeners(): void {
    if (!this.socket) return;

    // 연결 성공
    this.socket.on('connect', () => {
      console.log('🔌 Hospital Portal Socket connected:', this.socket?.id);
      this.connectionStatus.isConnected = true;
      this.connectionStatus.lastConnectedAt = new Date();
      this.reconnectAttempts = 0;
      this.connectionStatus.reconnectAttempts = 0;
      this.isReconnecting = false;
      
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
    });

    // 연결 해제
    this.socket.on('disconnect', (reason) => {
      console.log('❌ Hospital Portal Socket disconnected:', reason);
      this.connectionStatus.isConnected = false;
      this.connectionStatus.isAuthenticated = false;
      
      // 수동 disconnect가 아닌 경우에만 재연결 시도
      if (reason !== 'io client disconnect' && !document.hidden && this.currentToken) {
        this.wasConnected = true;
        this.handleReconnection();
      }
    });

    // 인증 응답 - 웹 환경에 최적화된 로깅
    this.socket.on('authenticated', (data: { success: boolean; message?: string; error?: string }) => {
      console.log('🔐 Hospital Portal Authentication response:', data);
      this.connectionStatus.isAuthenticated = data.success;
      
      if (!data.success) {
        this.connectionStatus.error = data.error || 'Authentication failed';
        console.error('🚫 Hospital Portal Authentication failed:', data.error);
      } else {
        console.log('✅ Hospital Portal authenticated successfully');
        delete this.connectionStatus.error;
        
        // 인증 성공 이벤트 발생
        window.dispatchEvent(new CustomEvent('socket_authenticated', { 
          detail: { success: true, socketId: this.socket?.id }
        }));
      }
    });

    // 실시간 알림 수신 - 병원 포털 전용 로깅
    this.socket.on('notification', (notification: HospitalNotification) => {
      console.log('🏥 Hospital notification received:', notification);
      this.handleNotificationReceived(notification);
    });

    // ✅ 추가: 채팅 메시지 관련 이벤트 핸들러들
    this.socket.on('new_message', (messageData: any) => {
      console.log('💬 Hospital Portal - New message received:', {
        messageData,
        timestamp: new Date().toISOString(),
        socketId: this.socket?.id,
        isConnected: this.connectionStatus.isConnected,
        isAuthenticated: this.connectionStatus.isAuthenticated
      });
      
      // 이벤트를 윈도우로 전파하여 ChatRoom 컴포넌트에서 수신할 수 있도록 함
      window.dispatchEvent(new CustomEvent('socket_new_message', { 
        detail: messageData 
      }));
    });

    this.socket.on('user_typing', (data: any) => {
      console.log('⌨️ Hospital Portal - User typing:', data);
      window.dispatchEvent(new CustomEvent('socket_user_typing', { 
        detail: data 
      }));
    });

    this.socket.on('messages_read', (data: any) => {
      console.log('👁️ Hospital Portal - Messages read:', data);
      window.dispatchEvent(new CustomEvent('socket_messages_read', { 
        detail: data 
      }));
    });

    this.socket.on('unread_count_updated', (data: any) => {
      console.log('📊 Hospital Portal - Unread count updated:', data);
      window.dispatchEvent(new CustomEvent('socket_unread_count_updated', { 
        detail: data 
      }));
    });

    this.socket.on('chat_room_joined', (data: any) => {
      console.log('🏠 Hospital Portal - Chat room joined:', data);
    });

    // 연결 에러 - 웹 환경 에러 처리
    this.socket.on('connect_error', (error: any) => {
      console.error('🚫 Hospital Portal Socket connection error:', error);
      console.error('🔍 Error details:', {
        message: error.message || 'Unknown error',
        description: error.description || 'No description',
        context: error.context || 'No context',
        type: error.type || 'Unknown type',
        transport: error.transport || 'Unknown transport'
      });
      console.log('🌐 Attempting to connect to:', this.SERVER_URL);
      this.connectionStatus.error = error.message || 'Connection error';
      
      if (!document.hidden && this.currentToken) {
        this.handleReconnection();
      }
    });
  }  // 웹 브라우저 환경 이벤트 리스너 설정
  private setupBrowserEventListeners(): void {
    // 페이지 가시성 변경 처리 (탭 전환 등)
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    
    // 페이지 언로드 전 정리
    window.addEventListener('beforeunload', this.handleBeforeUnload.bind(this));
    
    // 온라인/오프라인 상태 변경
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));
  }

  // 페이지 가시성 변경 처리 (웹 환경 전용)
  handleVisibilityChange(): void {
    if (document.hidden) {
      console.log('🌙 Hospital Portal tab became hidden, maintaining connection');
      // 웹에서는 탭이 숨겨져도 연결 유지 (짧은 기간)
    } else {
      console.log('☀️ Hospital Portal tab became visible');
      // 탭이 다시 활성화되면 연결 상태 확인
      if (!this.isConnected() && this.currentToken) {
        console.log('🔄 Reconnecting after tab activation...');
        this.reconnectWithBackoff();
      }
    }
  }

  // 페이지 언로드 전 정리 (웹 환경 전용)
  handleBeforeUnload(): void {
    console.log('👋 Hospital Portal page unloading, disconnecting socket');
    this.disconnect();
  }

  // 온라인 상태로 복귀
  private handleOnline(): void {
    console.log('🌐 Hospital Portal back online');
    if (this.currentToken && !this.isConnected()) {
      this.reconnectWithBackoff();
    }
  }

  // 오프라인 상태
  private handleOffline(): void {
    console.log('📵 Hospital Portal went offline');
    this.connectionStatus.error = 'Network offline';
  }  async connect(token: string): Promise<boolean> {
    this.currentToken = token; // 🔧 재연결을 위해 토큰 저장
    
    return new Promise((resolve) => {
      if (!this.socket) {
        this.initializeSocket();
      }

      if (this.connectionStatus.isConnected && this.connectionStatus.isAuthenticated) {
        resolve(true);
        return;
      }

      // 이미 재연결 중이면 중복 방지
      if (this.isReconnecting) {
        console.log('🔄 Already reconnecting, skipping...');
        resolve(false);
        return;
      }

      console.log('🔌 Hospital Portal connecting with token...');

      // 인증 완료 대기
      const authHandler = (data: { success: boolean }) => {
        this.socket?.off('authenticated', authHandler);
        resolve(data.success);
      };

      this.socket?.on('authenticated', authHandler);

      // 연결 및 인증
      if (!this.connectionStatus.isConnected) {
        this.socket?.connect();
      }

      // 연결된 후 인증
      const connectHandler = () => {
        this.socket?.off('connect', connectHandler);
        this.authenticate(token);
      };

      if (this.connectionStatus.isConnected) {
        this.authenticate(token);
      } else {
        this.socket?.on('connect', connectHandler);
      }

      // 타임아웃 처리 (웹 환경에서는 10초)
      setTimeout(() => {
        this.socket?.off('authenticated', authHandler);
        this.socket?.off('connect', connectHandler);
        if (!this.connectionStatus.isAuthenticated) {
          console.log('⏰ Hospital Portal connection timeout');
          resolve(false);
        }
      }, 10000);
    });
  }  private authenticate(token: string): void {
    console.log('🔐 Hospital Portal authenticating with token:', {
      tokenLength: token?.length,
      tokenPrefix: token?.substring(0, 20) + '...',
      socketId: this.socket?.id,
      socketConnected: this.socket?.connected
    });
    
    if (!this.socket?.connected) {
      console.error('❌ Cannot authenticate - socket not connected!');
      return;
    }
    
    this.socket.emit('authenticate', { token });
  }

  disconnect(): void {
    console.log('🔌 Hospital Portal disconnecting socket...');
    this.connectionStatus.isConnected = false;
    this.connectionStatus.isAuthenticated = false;
    this.isReconnecting = false;
    this.currentToken = null; // 토큰 초기화
    this.wasConnected = false;
    this.reconnectAttempts = 0;
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    this.socket?.disconnect();
  }

  onNotification(callback: NotificationCallback): void {
    this.notificationCallbacks.push(callback);
  }

  // 채팅 관련 메서드들
  joinChatRoom(roomId: string): void {
    console.log('🏥 Hospital Portal joining chat room:', {
      roomId,
      socketId: this.socket?.id,
      isConnected: this.connectionStatus.isConnected,
      isAuthenticated: this.connectionStatus.isAuthenticated
    });
    
    if (!this.socket || !this.connectionStatus.isConnected) {
      console.error('❌ Cannot join room - socket not connected');
      return;
    }
    
    this.socket.emit('join_chat_room', { chatRoomId: roomId });
    
    // 응답 리스너 등록
    this.socket.once('chat_room_joined', (data) => {
      console.log('✅ Successfully joined chat room:', data);
    });
  }

  leaveChatRoom(roomId: string): void {
    console.log('🏥 Hospital Portal leaving chat room:', roomId);
    this.socket?.emit('leave_chat_room', { chatRoomId: roomId }); // ✅ 수정
  }

  sendTypingStart(roomId: string): void {
    this.socket?.emit('typing_start', { chatRoomId: roomId }); // ✅ 수정
  }

  sendTypingEnd(roomId: string): void {
    this.socket?.emit('typing_end', { chatRoomId: roomId }); // ✅ 수정
  }

  private handleNotificationReceived(notification: HospitalNotification): void {
    // 웹 환경에서 브라우저 알림 표시 (권한이 있는 경우)
    if (Notification.permission === 'granted' && document.hidden) {
      new window.Notification(notification.title, {
        body: notification.message,
        icon: '/favicon.ico',
        tag: notification.id
      });
    }

    this.notificationCallbacks.forEach(callback => {
      try {
        callback(notification);
      } catch (error) {
        console.error('Error in Hospital Portal notification callback:', error);
      }
    });
  }

  markNotificationRead(notificationId: string): void {
    console.log('✓ Hospital Portal marking notification as read:', notificationId);
    this.socket?.emit('mark_notification_read', { notificationId });
  }

  isConnected(): boolean {
    return this.connectionStatus.isConnected && this.connectionStatus.isAuthenticated;
  }

  getConnectionStatus(): SocketConnectionStatus {
    return { ...this.connectionStatus };
  }  private handleReconnection(): void {
    // reconnectWithBackoff 메서드로 통합하여 사용
    this.reconnectWithBackoff();
  }

  // 🔧 지수 백오프를 사용한 재연결
  private async reconnectWithBackoff(): Promise<void> {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('❌ Hospital Portal max reconnection attempts reached');
      this.connectionStatus.error = 'Max reconnection attempts reached';
      this.reconnectAttempts = 0;
      return;
    }

    if (this.isReconnecting) {
      return;
    }

    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts), 30000); // 최대 30초
    console.log(`⏳ Hospital Portal reconnecting in ${delay}ms (attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`);

    this.isReconnecting = true;
    this.connectionStatus.reconnectAttempts = this.reconnectAttempts;

    this.reconnectTimer = setTimeout(async () => {
      this.reconnectAttempts++;
      
      try {
        if (this.currentToken) {
          const success = await this.connect(this.currentToken);
          if (success) {
            console.log('✅ Hospital Portal reconnection successful');
            this.reconnectAttempts = 0;
            this.isReconnecting = false;
          } else {
            this.isReconnecting = false;
            await this.reconnectWithBackoff();
          }
        }
      } catch (error) {
        console.error('❌ Hospital Portal reconnection failed:', error);
        this.isReconnecting = false;
        await this.reconnectWithBackoff();
      }
    }, delay);
  }

  // 브라우저 알림 권한 요청 (웹 환경 전용)
  async requestNotificationPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.log('Browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await window.Notification.requestPermission();
      return permission === 'granted';
    }

    return false;
  }

  // 정리 함수 (컴포넌트 언마운트 시 호출)
  cleanup(): void {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    window.removeEventListener('beforeunload', this.handleBeforeUnload.bind(this));
    window.removeEventListener('online', this.handleOnline.bind(this));
    window.removeEventListener('offline', this.handleOffline.bind(this));
    this.disconnect();
  }

  // socket 인스턴스 반환용 public 메서드 추가
  public getSocket(): Socket | null {
    return this.socket;
  }
}

// 병원 포털용 싱글톤 인스턴스
export const hospitalSocketService = new HospitalSocketService();
export default hospitalSocketService;